import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { UrlService } from '@core/services/url/url.service';
import { PreviewsService } from '@core/services/previews/previews.service';
import { File } from '@core/objects/file';

import { SettingsOrganizationService } from '@app/services/settings/organization.service';

class OrganizationLogoDragEditor {

    protected dragPosition: any;
    public diff: any;

    constructor(x: number, y: number) {
        this.dragPosition = {
            x: x,
            y: y
        };

        this.diff = {
            x: 0,
            y: 0
        }
    }

    setPosition(x: number, y: number) {
        this.diff = {
            x: x - this.dragPosition.x,
            y: y - this.dragPosition.y
        };

        this.dragPosition = {
            x: x,
            y: y
        }

    }
}

class OrganizationLogoEditor {
    public sizes: any;
    public position: any;
    public styles: any;
    public zoom: any;
    public scaleFactor: number;
    protected lastZoom: number = null;
    protected dragger: OrganizationLogoDragEditor;

    protected imageLoad: boolean = false;

    constructor() {
        this.sizes = {
            original: {width: 0, height: 0},
            image: {width: 0, height: 0},
            area: {width: 0, height: 0},
            // New property for crop width
            cropWidth: 300,
            // New property for crop height
            cropHeight: 150
        }

        this.position = {
            image: {
                left: 0,
                top: 0
            }
        };

        this.zoom = {
            relative: 0,
            current: 0
        }

        this.styles = {
            image: {
                'height.px': 0,
                'width.px': 0,
                'top.px': 0,
                'left.px':0
            }
        }

        this.dragger = null;
    }

    setAreaSize(width: number, height: number) {
        this.sizes.area = {
            width: width,
            height: height
        };
        this.update();
    }

    setOriginalSize(width: number, height: number) {
        this.sizes.original = {
            width: width,
            height: height
        };

        this.imageLoad = true;

        this.update();
    }

    setZoom(zoom: number) {
        this.lastZoom = zoom;
        this.zoom.relative = Math.round((this.zoom.relative + zoom)*10)/10;

        if(this.zoom.relative>1)
            this.zoom.relative = 1;

        if(this.zoom.relative<0)
            this.zoom.relative = 0;

        this.update();
    }

    drag(eventName, event) {
        // if(this.dragger!=null)
        //     console.log(eventName + "-->", event, this);

        switch(eventName) {
            case 'start':
                this.dragger = new OrganizationLogoDragEditor(event.screenX, event.screenY);
                break;
            case 'move':
                if(this.dragger!= null) {
                    this.dragger.setPosition(event.screenX, event.screenY);
                }
                break;
            case 'stop':
                this.dragger = null;
                break;
        }

        if(this.dragger!=null)
            this.update();
    }

    /**
     * Calculation Logic: The calculation of minp should be based on the relationship between the crop dimensions 
     * and the original image dimensions. It should identify the dimension (width or height) 
     * that needs to be scaled the most to fit the crop area and 
     * calculate the corresponding scaling factor.
     */
    update() {
        // if sizes are 0 then image is still not loaded ==> wait
        if( this.sizes.original.height == 0 && this.sizes.original.width == 0)
                return;

        // Calculate the scaling factor based on the larger dimension (width in this case)
        let minp;
        if (this.sizes.original.height / this.sizes.cropHeight < this.sizes.original.width / this.sizes.cropWidth){
            // Height needs more scaling to fit the crop area
            minp = this.sizes.cropHeight/this.sizes.original.height;
        } else {
            // Width needs more scaling to fit the crop area
            minp = this.sizes.cropWidth/this.sizes.original.width
        }
        this.scaleFactor = minp;
        let diffpu = (1 - minp) / 10;
        let sump = diffpu * (10 * this.zoom.relative);
        this.zoom.current = minp + sump;

        this.sizes.image = {
            height: this.sizes.original.height * this.zoom.current,
            width: this.sizes.original.width * this.zoom.current
        }
        // console.log(" DEBUG: editor sizes -->", this.zoom, this.sizes);

        // Center image on init
        if (this.imageLoad) {
            if (this.sizes.original.height > this.sizes.original.width) {
                this.position.image = {
                    top: (this.sizes.area.height - this.sizes.image.height) / 2,
                left: 0
                };
            } else {
                this.position.image = {
                    top: 0,
                    left: (this.sizes.area.width - this.sizes.image.width) / 2
                };
            }

            this.imageLoad = false;
        }

        // if zoomed (center image if zoom)
        let offsetPosition = {
            top: 0,
            left: 0
        };

        if(this.lastZoom != null) {
            // calc previous width
            let psump = diffpu * (10*(this.zoom.relative - this.lastZoom));
            let previousZoom = minp + psump;
            let previousSizes = {
                width: this.sizes.original.width * previousZoom,
                height: this.sizes.original.height * previousZoom
            }

            offsetPosition = {
                top: (previousSizes.height - this.sizes.image.height) / 2,
                left: (previousSizes.width - this.sizes.image.width) / 2
            }

            this.lastZoom = 0;
        }
        // end zoomed

        // fix position with zoom

        this.position.image = {
            top: this.position.image.top + offsetPosition.top,
            left: this.position.image.left + offsetPosition.left,
        }

        if(this.dragger!= null) {
            this.position.image = {
                top: this.position.image.top + this.dragger.diff.y,
                left: this.position.image.left + this.dragger.diff.x
            }
        }

         // positions limits based no new ratio 2:1
        let maxLeft = (this.sizes.area.width / 2) - (this.sizes.cropWidth / 2);
        let minLeft = maxLeft + this.sizes.cropWidth - this.sizes.image.width;
        let minTop = -(this.sizes.image.height - this.sizes.cropHeight);
        let maxTop = 0;

        if(this.position.image.left > maxLeft)
            this.position.image.left = maxLeft;

        if(this.position.image.left < minLeft)
            this.position.image.left = minLeft;

        if(this.position.image.top > maxTop)
            this.position.image.top = maxTop;

        if(this.position.image.top < minTop)
            this.position.image.top = minTop;

        this.updateStyle();
    }

    updateStyle() {
        this.styles.image = {
            'height.px': this.sizes.image.height,
            'width.px': this.sizes.image.width,
            'top.px': this.position.image.top,
            'left.px': this.position.image.left
        }
        
    }

    getOptions() : any {
        let top: number = (this.sizes.original.height * Math.abs(this.position.image.top)) / this.sizes.image.height;

        let spaceBetween = (this.sizes.area.width/2) - (this.sizes.cropWidth/2);
        let offsetLeft = this.position.image.left<0? spaceBetween + Math.abs(this.position.image.left) : spaceBetween - this.position.image.left;
        let left:number = (this.sizes.original.width * offsetLeft) / this.sizes.image.width;

        let width = (this.sizes.original.width * Math.abs(this.sizes.cropWidth)) / this.sizes.image.width;
        let height = (this.sizes.original.height * Math.abs(this.sizes.cropHeight)) / this.sizes.image.height;

        // absolut crop / original = percent
        let crop = {
            top: Math.round(top) / this.sizes.original.height,
            left: Math.round(left) / this.sizes.original.width,
            width: Math.round(width) / this.sizes.original.width,
            height: Math.round(height) / this.sizes.original.height
        };

        return {
            crop: crop
        }


    }

    setOptions(options: any) {
        // apply crop
        if(options.hasOwnProperty('crop')) {
            // set zoom

            this.sizes.image.height = this.sizes.cropHeight/options.crop.height;
            this.sizes.image.width = this.sizes.cropWidth/options.crop.width;

            let left = this.sizes.image.width * options.crop.left;
            let maxLeft = (this.sizes.area.width/2) - (this.sizes.cropWidth/2);

            left = maxLeft - left;

            let top = -1*(this.sizes.image.height * options.crop.top);
            this.position.image.left = left;
            this.position.image.top = top;

            // zoom
            let minp;
            if (this.sizes.original.height / this.sizes.cropHeight < this.sizes.original.width / this.sizes.cropWidth){
                minp = this.sizes.cropHeight/this.sizes.original.height;
            } else {
                minp = this.sizes.cropWidth/this.sizes.original.width
            }
            let diffpu = (1-minp) / 10; //  (0, 0.1, 0.2... until 1)

            this.zoom.current =  this.sizes.image.height / this.sizes.original.height;
            this.zoom.relative = Math.round((this.zoom.current-minp)/diffpu)/10;
            this.lastZoom = 0;
        }

        this.updateStyle();
    }

}

@Component({
    selector: 'organization-profile-logo-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.scss']
})
export class OrganizationProfileLogoEditComponent implements OnInit, OnDestroy {

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_FOUND = 2;

    public readonly RESPONSE_ERROR_FORBIDDEN = 403;
    public readonly MODAL_VIEW_NONE = 0;
    public readonly MODAL_VIEW_LOGO_UPLOAD_ERROR = 1;

    @ViewChild('drag') area: ElementRef;

    @Input() file: File;
    @Input() options: any;

    @Output() cancel = new EventEmitter<any>();
    @Output() save = new EventEmitter<any>();

    // state status observables
    updateStatus$: Observable<number>;
    uploadStatus$: Observable<number>;  
    deleteStatus$: Observable<number>;  
    errorActions$: Observable<HttpErrorResponse>;  

    protected previewSubscriptor$: Subscription;
    protected uploadStatusSubscriptor$: Subscription;
    public modalView: number = this.MODAL_VIEW_NONE;
    // image properties
    public urlImage:string = null; //URL image
    protected img: any; // object for load image and get original sizes;
    public imgLoaded: boolean = false;
    public working: boolean = false;

    public editor: OrganizationLogoEditor;
    public styles: any;

    constructor(public urlSrv: UrlService, protected prwSrv: PreviewsService, 
        private orgSrv: SettingsOrganizationService) {
        this.editor = new OrganizationLogoEditor();

        this.styles = {
            area: {
                'transform-origin': "0% 0%",
                'background-size': "100%",
                'background-position-x.px': 0,
                'background-position-y.px': 0,
                'transform': null,
                'width': 'inherit'
            },
            image: {
                'height.px': 150,
                // Adjusted for new aspect ratio
                'width': 'inherit'
            }
        };
    }

	ngOnInit(){
        // initialize subscribers
        this.uploadStatus$ = this.orgSrv.selectUploadStatus(); // Use the service method
        this.deleteStatus$ = this.orgSrv.selectDeleteStatus(); // Use the service method
        this.errorActions$ = this.orgSrv.selectErrorActions(); // Use the service method

        // take new fiel to reload the preview
        this.previewSubscriptor$ = this.prwSrv.getSubscriptor(this.file).subscribe(p => {
            if(p.status_code == 1)
                this.urlImage = this.urlSrv.build('/files/'+this.file.id+'/preview/big-width/1');
        });
        // Subscribe to uploadStatus$ and trigger reload/refresh when done
        this.uploadStatusSubscriptor$ = this.orgSrv.selectUploadStatus().subscribe(status => {
            switch (status) {
                case 1: // Loading
                    this.working = true;
                    break;
                case 2: // Done
                case 0: // Assuming 0 is also a "Done" state
                    this.working = false;
                    break;
                case 3: // Error
                    this.working = false;
                    this.modalView = this.MODAL_VIEW_LOGO_UPLOAD_ERROR;
                    break;
            }
        });
        this.prwSrv.load(this.file);

    }

    ngOnDestroy() {
        this.previewSubscriptor$.unsubscribe();
        this.uploadStatusSubscriptor$.unsubscribe();
    }

    imageLoaded(event) {
        this.onImageLoaded();


        this.editor.setOriginalSize(event.currentTarget.width, event.currentTarget.height);
        this.editor.setOptions(this.options);


    }

    onError(){
        this.orgSrv.clearActionFailures();
        this.modalView = this.MODAL_VIEW_NONE;
    }

    resizeArea(event) {
        this.editor.setAreaSize(event.width, event.height);
    }

  
    /**
     * Dispatched when image is loaded
     **/
    protected onImageLoaded() {
        if(!this.imgLoaded) {
            this.imgLoaded = true;
        }
    }

    test() {
        this.imgLoaded = !this.imgLoaded;
    }

    submit() {
        // console.log("submit", this.editor.getOptions());
        this.save.emit(this.editor.getOptions());

    }

    onCancel() {
        this.cancel.emit(true);
    }
}